import { useMemo, useRef, useState } from 'react';
import { Check, Plus } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useIsMobile } from '@/hooks/use-mobile';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import type { SavedCollection } from '@/lib/saved-api';

interface AddToListsSheetProps {
  /**
   * Trigger / anchor element — typically the chip on a saved card or the
   * heart on a Shop card. In uncontrolled mode (no `open` prop) clicking it
   * opens the sheet. In controlled mode the parent decides; the element
   * just provides the anchor position for the desktop popover.
   */
  children: React.ReactNode;
  /**
   * Pass to put the sheet in controlled mode. Use this when the trigger has
   * its own click semantics (e.g. SaveButton, where tap = save, not open).
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  collections: SavedCollection[];
  /** Lists this saved item already belongs to. */
  selected: string[];
  onAdd: (collectionId: string) => void;
  onRemove: (collectionId: string) => void;
  onCreate: (name: string) => Promise<SavedCollection>;
}

/**
 * Membership editor for one saved item. Bottom sheet on mobile, popover
 * on desktop — the body is the same, only the chrome changes. The trigger
 * lives inside the component so Radix anchors naturally.
 */
export function AddToListsSheet({
  children,
  open,
  onOpenChange,
  collections,
  selected,
  onAdd,
  onRemove,
  onCreate,
}: AddToListsSheetProps) {
  const isMobile = useIsMobile();
  const controlled = open !== undefined;

  const body = (
    <ListsEditor
      collections={collections}
      selected={selected}
      onAdd={onAdd}
      onRemove={onRemove}
      onCreate={onCreate}
    />
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        {/* Mobile sheet is full-bleed bottom sheet — no anchor needed.
            In controlled mode we still render the children but skip the
            Trigger wrapper so the parent's click handler is the only
            thing that opens it. */}
        {controlled ? children : <SheetTrigger asChild>{children}</SheetTrigger>}
        <SheetContent
          side="bottom"
          // dvh tracks the visual viewport on iOS — when the soft keyboard
          // opens, the sheet shrinks with it instead of getting cut off
          // behind the keyboard. The flex column lets the inner list area
          // scroll while the header + create-list row stay pinned.
          className="flex max-h-[85dvh] flex-col rounded-t-2xl border-light-taupe bg-warm-white p-0"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <SheetHeader className="shrink-0 border-b border-light-taupe/60 px-5 py-4 text-left">
            <SheetTitle className="font-serif text-lg text-charcoal">
              Add to lists
            </SheetTitle>
            <SheetDescription className="text-xs text-charcoal/60">
              Pick the lists this piece belongs in. Untick to remove.
            </SheetDescription>
          </SheetHeader>
          {body}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      {/* In controlled mode the visible button has its own click semantics
          (e.g. SaveButton: tap = save). PopoverAnchor positions the popover
          without intercepting clicks; PopoverTrigger would auto-toggle on
          every click and force us to swallow the event. */}
      {controlled ? (
        <PopoverAnchor asChild>{children}</PopoverAnchor>
      ) : (
        <PopoverTrigger asChild>{children}</PopoverTrigger>
      )}
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={8}
        // Same flex-column / bounded-height shape as the mobile sheet so
        // the inner list scrolls when there are many collections.
        className="flex max-h-[28rem] w-72 flex-col border-light-taupe bg-warm-white p-0 shadow-lg"
        onOpenAutoFocus={(e) => {
          // Don't pull focus to the first checkbox on open — flashes a
          // focus ring on the wrong row. The create input below earns
          // focus only when the user explicitly types.
          e.preventDefault();
        }}
      >
        <div className="shrink-0 border-b border-light-taupe/60 px-4 py-3">
          <p className="font-serif text-sm font-semibold text-charcoal">
            Add to lists
          </p>
          <p className="mt-0.5 text-[11px] text-charcoal/55">
            Tick the lists this piece belongs in.
          </p>
        </div>
        {body}
      </PopoverContent>
    </Popover>
  );
}

interface ListsEditorProps {
  collections: SavedCollection[];
  selected: string[];
  onAdd: (collectionId: string) => void;
  onRemove: (collectionId: string) => void;
  onCreate: (name: string) => Promise<SavedCollection>;
}

function ListsEditor({
  collections,
  selected,
  onAdd,
  onRemove,
  onCreate,
}: ListsEditorProps) {
  const [draft, setDraft] = useState('');
  const [creating, setCreating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = draft.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const collection = await onCreate(name);
      onAdd(collection.id);
      setDraft('');
      // Tapping the "Create" button moved focus off the input — on mobile
      // that dismisses the soft keyboard. Pull focus back so the customer
      // can keep adding lists without a second tap into the field.
      inputRef.current?.focus();
      toast({
        title: `Created "${collection.name}"`,
        description: 'Added this piece to it.',
      });
    } catch (err) {
      toast({
        title: 'Could not create list',
        description: err instanceof Error ? err.message : undefined,
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  };

  const canSubmit = draft.trim().length > 0 && !creating;

  return (
    // min-h-0 + flex-1 lets the editor fill its parent (sheet/popover) so
    // the list region can shrink and scroll when the keyboard takes the
    // bottom half of the screen.
    <div className="flex min-h-0 flex-1 flex-col">
      <form
        onSubmit={handleCreate}
        className="flex shrink-0 items-center gap-2 border-b border-light-taupe/60 px-4 py-3"
      >
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-soft-blush/15 text-soft-blush">
          <Plus className="h-3.5 w-3.5" />
        </div>
        <Input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="New list, e.g. Workwear"
          maxLength={80}
          // Input stays enabled while creating; only the submit button is
          // disabled. Disabling the input during the ~150ms create round-trip
          // would dismiss the soft keyboard and break the "create another
          // list and another" flow.
          className="h-9 border-0 bg-transparent px-0 text-sm shadow-none focus-visible:ring-0"
        />
        {/* Always-rendered to avoid the layout-shift the conditional render
            caused as the customer typed the first character. Disabled state
            communicates "type something first" without the surface jumping. */}
        <button
          type="submit"
          disabled={!canSubmit}
          aria-label="Create list"
          className="shrink-0 rounded-full bg-soft-blush px-3 py-1 text-xs font-semibold text-white transition hover:bg-dusty-rose disabled:opacity-30 disabled:hover:bg-soft-blush"
        >
          Create
        </button>
      </form>

      {collections.length === 0 ? (
        <div className="shrink-0 px-4 py-6 text-center text-xs text-charcoal/55">
          No lists yet. Type above to create your first.
        </div>
      ) : (
        <ul
          role="listbox"
          aria-label="Lists"
          className="min-h-0 flex-1 overflow-y-auto py-1"
        >
          {collections.map((c) => {
            const isIn = selectedSet.has(c.id);
            return (
              <li key={c.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isIn}
                  onClick={() => (isIn ? onRemove(c.id) : onAdd(c.id))}
                  className={cn(
                    'flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors',
                    'hover:bg-cream/60',
                    isIn ? 'text-charcoal' : 'text-charcoal/80',
                  )}
                >
                  <Checkbox
                    checked={isIn}
                    aria-hidden="true"
                    tabIndex={-1}
                    className="pointer-events-none border-light-taupe data-[state=checked]:border-soft-blush data-[state=checked]:bg-soft-blush"
                  />
                  <span className="flex-1 truncate">{c.name}</span>
                  {isIn && (
                    <Check className="h-3.5 w-3.5 text-soft-blush" aria-hidden="true" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
